import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';

function IntakeForm() {
  const { companyCode } = useParams();
  const { getAppName } = useConfig();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [leadSources, setLeadSources] = useState([]);
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(true);
  const [disclaimerText, setDisclaimerText] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: 'WI',
    zip: '',
    phone: '',
    email: '',
    leadSource: '',
    leadSourceOther: '',
    referredByName: '',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    notes: '',
    serviceFrequency: '',
    servicePreference: 'on_call',
    // Honeypot field
    website: '',
    // Terms acceptance
    termsAccepted: false,
    emailSubscribed: true,
  });

  // Additional properties state
  const [additionalProperties, setAdditionalProperties] = useState([]);
  const [expandedProperties, setExpandedProperties] = useState({});

  useEffect(() => {
    const loadFormData = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const fullUrl = `${apiUrl}/api/public/intake/${companyCode}`;

      try {
        const response = await axios.get(fullUrl);
        setCompanyName(response.data.companyName);
        setLeadSources(response.data.leadSources);
        // Load disclaimer settings from company
        setDisclaimerEnabled(response.data.disclaimerEnabled ?? true);
        setDisclaimerText(response.data.disclaimerText || '');
        setError('');
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This signup form is not available. Please check the link and try again.');
        } else {
          // Include detailed error info for debugging on mobile
          const errorDetails = [];
          if (err.message) errorDetails.push(err.message);
          if (err.response?.status) errorDetails.push(`Status: ${err.response.status}`);
          if (err.response?.data?.error) errorDetails.push(err.response.data.error);
          if (err.code) errorDetails.push(`Code: ${err.code}`);

          const detailStr = errorDetails.length > 0 ? ` (${errorDetails.join(', ')})` : '';
          setError(`Unable to load form${detailStr}. Please try again later.`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (companyCode) {
      loadFormData();
    } else {
      setError('Invalid signup link. Please contact us directly.');
      setLoading(false);
    }
  }, [companyCode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const addProperty = () => {
    const newProperty = {
      id: Date.now(), // Temporary ID for React key
      address: '',
      city: formData.city || '',
      state: formData.state || 'WI',
      zip: '',
      driveway: true,
      sidewalks: false,
      walkways: false,
      salt: false,
      notes: '',
    };
    setAdditionalProperties(prev => [...prev, newProperty]);
    setExpandedProperties(prev => ({ ...prev, [newProperty.id]: true }));
  };

  const removeProperty = (propertyId) => {
    setAdditionalProperties(prev => prev.filter(p => p.id !== propertyId));
    setExpandedProperties(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[propertyId];
      return newExpanded;
    });
  };

  const handlePropertyChange = (propertyId, e) => {
    const { name, value, type, checked } = e.target;
    setAdditionalProperties(prev => prev.map(p => {
      if (p.id === propertyId) {
        return { ...p, [name]: type === 'checkbox' ? checked : value };
      }
      return p;
    }));
  };

  const togglePropertyExpanded = (propertyId) => {
    setExpandedProperties(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate service frequency selection
    if (!formData.serviceFrequency) {
      setError('Please select your service frequency');
      setSubmitting(false);
      return;
    }

    // Validate terms acceptance (only if disclaimer is enabled)
    if (disclaimerEnabled && !formData.termsAccepted) {
      setError('You must agree to the Payment Terms & Conditions');
      setSubmitting(false);
      return;
    }

    try {
      // Prepare additional properties (remove temporary IDs)
      const cleanedProperties = additionalProperties.map(({ id, ...rest }) => rest);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/public/intake/${companyCode}`,
        {
          ...formData,
          additionalProperties: cleanedProperties,
        }
      );
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h1>Thank You!</h1>
            <p>Your information has been submitted successfully.</p>
            <p>We will contact you shortly to discuss your lawn care needs.</p>
            {companyName && <p className="company-name">— {companyName}</p>}
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error && !companyName) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="error-message">
            <h1>Oops!</h1>
            <p>{error}</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <div className="intake-container">
        <div className="intake-header">
          <h1>{companyName || getAppName()}</h1>
          <p className="intake-subtitle">New Customer Sign Up</p>
        </div>

        <form onSubmit={handleSubmit} className="intake-form">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-section">
            <h2>Contact Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  placeholder="(555) 555-5555"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  placeholder="your@email.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section property-section">
            <h2>
              <span className="property-number">Property 1</span>
              {additionalProperties.length > 0 && <span className="primary-badge">Primary</span>}
            </h2>

            <div className="form-group">
              <label className="form-label">Street Address *</label>
              <input
                type="text"
                name="address"
                className="form-input"
                value={formData.address}
                onChange={handleChange}
                required
                autoComplete="street-address"
                placeholder="123 Main St"
              />
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  autoComplete="address-level2"
                />
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  autoComplete="address-level1"
                  placeholder="OH"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP *</label>
                <input
                  type="text"
                  name="zip"
                  className="form-input"
                  value={formData.zip}
                  onChange={handleChange}
                  required
                  autoComplete="postal-code"
                  placeholder="44xxx"
                />
              </div>
            </div>

            <div className="checkbox-grid property-services">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="driveway"
                  checked={formData.driveway}
                  onChange={handleChange}
                />
                <span>Lawn Mowing</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="sidewalks"
                  checked={formData.sidewalks}
                  onChange={handleChange}
                />
                <span>Edging</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="walkways"
                  checked={formData.walkways}
                  onChange={handleChange}
                />
                <span>Trimming</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="salt"
                  checked={formData.salt}
                  onChange={handleChange}
                />
                <span>Leaf Cleanup</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Notes for this property</label>
              <textarea
                name="notes"
                className="form-textarea"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Gate codes, special instructions..."
              />
            </div>
          </div>

          {/* Additional Properties */}
          {additionalProperties.map((property, index) => (
            <div key={property.id} className="form-section property-section additional-property">
              <div className="property-header">
                <h2 onClick={() => togglePropertyExpanded(property.id)} className="property-toggle">
                  <span className="toggle-icon">{expandedProperties[property.id] ? '▼' : '▶'}</span>
                  <span className="property-number">Property {index + 2}</span>
                  {property.address && <span className="property-preview">- {property.address}</span>}
                </h2>
                <button
                  type="button"
                  className="btn-remove-property"
                  onClick={() => removeProperty(property.id)}
                  title="Remove this property"
                >
                  Remove
                </button>
              </div>

              {expandedProperties[property.id] && (
                <div className="property-fields">
                  <div className="form-group">
                    <label className="form-label">Street Address *</label>
                    <input
                      type="text"
                      name="address"
                      className="form-input"
                      value={property.address}
                      onChange={(e) => handlePropertyChange(property.id, e)}
                      required
                      placeholder="456 Oak Ave"
                    />
                  </div>

                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        className="form-input"
                        value={property.city}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        name="state"
                        className="form-input"
                        value={property.state}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                        maxLength={2}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ZIP *</label>
                      <input
                        type="text"
                        name="zip"
                        className="form-input"
                        value={property.zip}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                      />
                    </div>
                  </div>

                  <div className="checkbox-grid property-services">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={property.driveway}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Lawn Mowing</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={property.sidewalks}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Edging</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={property.walkways}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Trimming</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="salt"
                        checked={property.salt}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Leaf Cleanup</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes for this property</label>
                    <textarea
                      name="notes"
                      className="form-textarea"
                      value={property.notes}
                      onChange={(e) => handlePropertyChange(property.id, e)}
                      rows={2}
                      placeholder="Gate codes, special instructions..."
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add Another Property Button */}
          <div className="add-property-section">
            <button
              type="button"
              className="btn-add-property"
              onClick={addProperty}
            >
              + Add Another Property
            </button>
            <p className="add-property-hint">Have multiple addresses? Add them all here.</p>
          </div>

          {leadSources.length > 0 && (
            <div className="form-section">
              <h2>How did you find us?</h2>
              <div className="form-group">
                <select
                  name="leadSource"
                  className="form-select"
                  value={formData.leadSource}
                  onChange={handleChange}
                >
                  <option value="">-- Select --</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
              {formData.leadSource === 'referral' && (
                <div className="form-group">
                  <label className="form-label">Who referred you?</label>
                  <input
                    type="text"
                    name="referredByName"
                    className="form-input"
                    value={formData.referredByName}
                    onChange={handleChange}
                    placeholder="Referrer's name"
                  />
                </div>
              )}
              {formData.leadSource === 'other' && (
                <div className="form-group">
                  <label className="form-label">Please specify</label>
                  <input
                    type="text"
                    name="leadSourceOther"
                    className="form-input"
                    value={formData.leadSourceOther}
                    onChange={handleChange}
                    placeholder="How did you hear about us?"
                  />
                </div>
              )}
            </div>
          )}

          <div className="form-section">
            <h2>How often would you like service? *</h2>
            <div className="form-group">
              <select
                name="serviceFrequency"
                className="form-select"
                value={formData.serviceFrequency}
                onChange={handleChange}
                required
              >
                <option value="">-- Select --</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
                <option value="on_call">As needed / On-call</option>
              </select>
              <p className="field-hint">Select how often you'd like lawn care service.</p>
            </div>
          </div>

          <div className="form-section">
            <h2>Service Type *</h2>
            <div className="form-group">
              <div className="radio-options">
                <label className="radio-item">
                  <input
                    type="radio"
                    name="servicePreference"
                    value="seasonal"
                    checked={formData.servicePreference === 'seasonal'}
                    onChange={handleChange}
                  />
                  <span className="radio-label">
                    <strong>Seasonal Contract</strong>
                    <span className="radio-description">Regular service throughout the growing season</span>
                  </span>
                </label>
                <label className="radio-item">
                  <input
                    type="radio"
                    name="servicePreference"
                    value="on_call"
                    checked={formData.servicePreference === 'on_call'}
                    onChange={handleChange}
                  />
                  <span className="radio-label">
                    <strong>On-Call / As Needed</strong>
                    <span className="radio-description">Pay per service when lawn care is performed</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Honeypot field - hidden from users */}
          <div style={{ position: 'absolute', left: '-9999px' }}>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Payment Terms & Conditions Disclaimer */}
          {disclaimerEnabled && disclaimerText && (
            <div className="terms-disclaimer">
              <h3>Payment Terms & Conditions</h3>
              <p className="terms-text">
                By submitting this form, you acknowledge and agree to the following terms:
              </p>
              <p className="terms-text">
                {disclaimerText.replace(/{company_name}/g, companyName)}
              </p>
              <label className="terms-checkbox">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                />
                <span>I have read and agree to the Payment Terms & Conditions <span className="required">*</span></span>
              </label>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="emailSubscribed"
                checked={formData.emailSubscribed}
                onChange={handleChange}
                style={{ marginTop: '3px' }}
              />
              <div>
                <span style={{ fontWeight: 500 }}>Subscribe to email updates</span>
                <p style={{ fontSize: '13px', color: '#666', margin: '2px 0 0' }}>
                  Receive notifications about service schedules, seasonal updates, and important announcements
                </p>
              </div>
            </label>
          </div>

          <button type="submit" className="btn btn-primary btn-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>

          <p className="form-footer">
            By submitting this form, you agree to be contacted about lawn care services.
          </p>
        </form>
      </div>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .intake-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #1b5e20 0%, #4CAF50 100%);
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .intake-container {
    background: white;
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    padding: 32px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  .intake-header {
    text-align: center;
    margin-bottom: 32px;
  }

  .intake-header h1 {
    font-size: 1.75rem;
    color: #2e7d32;
    margin: 0 0 8px 0;
  }

  .intake-subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
  }

  .form-section {
    margin-bottom: 24px;
  }

  .form-section h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-row-3 {
    grid-template-columns: 2fr 1fr 1fr;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  .form-input,
  .form-select,
  .form-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .form-input:focus,
  .form-select:focus,
  .form-textarea:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .form-textarea {
    resize: vertical;
    min-height: 80px;
  }

  .checkbox-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    transition: background 0.2s;
  }

  .checkbox-item:hover {
    background: #f3f4f6;
  }

  .checkbox-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #4CAF50;
  }

  .checkbox-item span {
    font-size: 0.95rem;
    color: #374151;
  }

  .btn-submit {
    width: 100%;
    padding: 14px;
    font-size: 1.1rem;
    font-weight: 600;
    background: #4CAF50;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }

  .btn-submit:hover:not(:disabled) {
    background: #388E3C;
  }

  .btn-submit:active:not(:disabled) {
    transform: scale(0.98);
  }

  .btn-submit:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .form-footer {
    text-align: center;
    font-size: 0.8rem;
    color: #9ca3af;
    margin-top: 16px;
  }

  .alert {
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 0.9rem;
  }

  .alert-error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }

  .success-message {
    text-align: center;
    padding: 40px 20px;
  }

  .success-icon {
    width: 64px;
    height: 64px;
    background: #10b981;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    margin: 0 auto 24px;
  }

  .success-message h1 {
    color: #2e7d32;
    margin: 0 0 16px 0;
  }

  .success-message p {
    color: #6b7280;
    margin: 0 0 8px 0;
  }

  .success-message .company-name {
    font-style: italic;
    margin-top: 24px;
    color: #2e7d32;
  }

  .error-message {
    text-align: center;
    padding: 40px 20px;
  }

  .error-message h1 {
    color: #dc2626;
    margin: 0 0 16px 0;
  }

  .error-message p {
    color: #6b7280;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #4CAF50;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Multiple Properties Styles */
  .property-section {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    background: #fafafa;
  }

  .property-section h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 16px;
  }

  .property-number {
    color: #2e7d32;
  }

  .primary-badge {
    font-size: 0.7rem;
    background: #4CAF50;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 500;
  }

  .property-services {
    margin-top: 12px;
  }

  .additional-property {
    border-color: #7c3aed;
    background: #faf5ff;
  }

  .property-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .property-toggle {
    cursor: pointer;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    border: none;
    padding: 0;
  }

  .toggle-icon {
    color: #7c3aed;
    font-size: 0.8rem;
  }

  .property-preview {
    font-weight: 400;
    color: #6b7280;
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .btn-remove-property {
    background: none;
    border: 1px solid #dc2626;
    color: #dc2626;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-remove-property:hover {
    background: #dc2626;
    color: white;
  }

  .property-fields {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9d5ff;
  }

  .add-property-section {
    text-align: center;
    padding: 20px;
    margin-bottom: 24px;
  }

  .btn-add-property {
    background: white;
    border: 2px dashed #7c3aed;
    color: #7c3aed;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-add-property:hover {
    background: #7c3aed;
    color: white;
    border-style: solid;
  }

  .add-property-hint {
    margin: 8px 0 0 0;
    font-size: 0.8rem;
    color: #9ca3af;
  }

  .field-hint {
    margin: 6px 0 0 0;
    font-size: 0.8rem;
    color: #6b7280;
  }

  /* Terms Disclaimer Styles */
  .terms-disclaimer {
    background: #f9fafb;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .terms-disclaimer h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
  }

  .terms-text {
    font-size: 0.9em;
    color: #4b5563;
    line-height: 1.6;
    margin: 0 0 12px 0;
  }

  .terms-text:last-of-type {
    margin-bottom: 16px;
  }

  .terms-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    padding: 12px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: border-color 0.2s;
  }

  .terms-checkbox:hover {
    border-color: #4CAF50;
  }

  .terms-checkbox input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #4CAF50;
    flex-shrink: 0;
  }

  .terms-checkbox span {
    font-size: 0.95rem;
    color: #374151;
    font-weight: 500;
  }

  .terms-checkbox .required {
    color: #dc2626;
    font-weight: 600;
  }

  /* Radio Options Styles */
  .radio-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    padding: 16px;
    background: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    transition: all 0.2s;
  }

  .radio-item:hover {
    border-color: #4CAF50;
    background: #f0f7ff;
  }

  .radio-item:has(input:checked) {
    border-color: #4CAF50;
    background: #eff6ff;
  }

  .radio-item input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #4CAF50;
    flex-shrink: 0;
  }

  .radio-label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .radio-label strong {
    font-size: 1rem;
    color: #2e7d32;
  }

  .radio-description {
    font-size: 0.85rem;
    color: #6b7280;
  }

  @media (max-width: 640px) {
    .intake-container {
      padding: 24px 20px;
    }

    .form-row,
    .form-row-3 {
      grid-template-columns: 1fr;
    }

    .checkbox-grid {
      grid-template-columns: 1fr;
    }

    .intake-header h1 {
      font-size: 1.5rem;
    }
  }
`;

export default IntakeForm;
